import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  message,
  Popconfirm,
  Select,
  Row,
  Col,
  Statistic,
  Badge,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface TaobaoReceiptCode {
  id: number;
  code: string;
  status: 'active' | 'used' | 'expired';
  created_at: string;
  used_at?: string;
  expires_at?: string;
  created_by?: number;
  notes?: string;
  created_by_name?: string;
  used_by_user_id?: number;
  used_on_server_id?: number;
  used_by_user_name?: string;
  used_on_server_name?: string;
}

// 简化版本：不再需要Server和User接口

const TaobaoReceiptCodes: React.FC = () => {
  const [codes, setCodes] = useState<TaobaoReceiptCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCode, setEditingCode] = useState<TaobaoReceiptCode | null>(null);
  const [form] = Form.useForm();

  // 数据源（简化版本，不再需要服务器和用户列表）

  // 批量操作
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  
  // 导出相关状态
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportForm] = Form.useForm();
  const [exportLoading, setExportLoading] = useState(false);

  // 加载收货码列表
  const loadCodes = useCallback(async (currentPage = 1, pageSize = 20, currentFilters = { status: '', search: '' }) => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        ...currentFilters
      };

      const response = await api.get('/taobao-receipt-codes/admin', { params });
      setCodes(response.data.codes || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0
      }));
    } catch (error) {
      console.error('加载收货码列表失败:', error);
      message.error('加载收货码列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 简化版本：不再需要加载服务器和用户列表

  useEffect(() => {
    loadCodes(pagination.current, pagination.pageSize, filters);
  }, [loadCodes, pagination.current, pagination.pageSize, filters]);

  // 简化版本：不再需要加载服务器和用户

  // 处理表格变化
  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
  };

  // 处理搜索
  const handleSearch = (values: any) => {
    setFilters(values);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    setFilters({ status: '', search: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 打开创建/编辑模态框
  const handleOpenModal = (code?: TaobaoReceiptCode) => {
    setEditingCode(code || null);
    setModalVisible(true);
    
    if (code) {
      form.setFieldsValue({
        ...code,
        expires_at: code.expires_at ? dayjs(code.expires_at) : null
      });
    } else {
      form.resetFields();
    }
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingCode(null);
    form.resetFields();
  };

  // 提交表单
  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        expires_at: values.expires_at ? values.expires_at.format('YYYY-MM-DD HH:mm:ss') : null
      };

      if (editingCode) {
        await api.put(`/taobao-receipt-codes/admin/${editingCode.id}`, data);
        message.success('收货码更新成功');
      } else if (values.count && values.count > 1) {
        // 批量创建
        await api.post('/taobao-receipt-codes/admin/batch-create', {
          count: values.count,
          notes: values.notes,
          expires_at: data.expires_at
        });
        message.success(`成功创建 ${values.count} 个收货码`);
      } else {
        // 单个创建
        await api.post('/taobao-receipt-codes/admin', data);
        message.success('收货码创建成功');
      }

      handleCloseModal();
      loadCodes(pagination.current, pagination.pageSize, filters);
    } catch (error: any) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 删除收货码
  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/taobao-receipt-codes/admin/${id}`);
      message.success('收货码删除成功');
      loadCodes(pagination.current, pagination.pageSize, filters);
    } catch (error: any) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的收货码');
      return;
    }

    try {
      await api.post('/taobao-receipt-codes/admin/batch-delete', {
        ids: selectedRowKeys
      });

      message.success(`成功删除 ${selectedRowKeys.length} 个收货码`);
      setSelectedRowKeys([]);
      setBatchModalVisible(false);
      loadCodes(pagination.current, pagination.pageSize, filters);
    } catch (error: any) {
      message.error('批量删除失败');
    }
  };

  // 批量导出收货码
  const handleExport = async (values: any) => {
    setExportLoading(true);
    try {
      const response = await api.post('/taobao-receipt-codes/export', values, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `taobao-receipt-codes-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('收货码导出成功');
      setExportModalVisible(false);
    } catch (error: any) {
      message.error(error.response?.data?.message || '导出失败');
    } finally {
      setExportLoading(false);
    }
  };

  // 获取状态标签
  const getStatusTag = (status: string) => {
    const statusMap = {
      active: { color: 'processing', text: '有效', icon: <CheckCircleOutlined /> },
      used: { color: 'success', text: '已使用', icon: <CheckCircleOutlined /> },
      expired: { color: 'error', text: '已过期', icon: <CloseCircleOutlined /> }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap];
    return (
      <Tag color={statusInfo.color} icon={statusInfo.icon}>
        {statusInfo.text}
      </Tag>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true
    },
    {
      title: '收货码',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (code: string) => (
        <code style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '2px 6px', 
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {code}
        </code>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status)
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      width: 150,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '使用用户',
      dataIndex: 'used_by_user_name',
      key: 'used_by_user_name',
      width: 120,
      render: (name: string, record: TaobaoReceiptCode) => 
        record.status === 'used' ? (name || `用户ID: ${record.used_by_user_id}`) : '-'
    },
    {
      title: '使用服务器',
      dataIndex: 'used_on_server_name',
      key: 'used_on_server_name',
      width: 150,
      ellipsis: true,
      render: (name: string, record: TaobaoReceiptCode) => 
        record.status === 'used' ? (name || `服务器ID: ${record.used_on_server_id}`) : '-'
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 150,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '创建者',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 100,
      render: (name: string) => name || '系统'
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      width: 150,
      ellipsis: true,
      render: (notes: string) => notes ? (
        <Tooltip title={notes}>
          <span>{notes}</span>
        </Tooltip>
      ) : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: TaobaoReceiptCode) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个收货码吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record: TaobaoReceiptCode) => ({
      disabled: record.status === 'used' // 已使用的不能选择
    })
  };

  // 统计数据
  const stats = {
    total: codes.length,
    active: codes.filter(c => c.status === 'active').length,
    used: codes.filter(c => c.status === 'used').length,
    expired: codes.filter(c => c.status === 'expired').length
  };

  return (
    <div>
      <Card title="淘宝收货码管理" className="mb-4">
        {/* 统计卡片 */}
        <Row gutter={16} className="mb-4">
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="总计"
                value={stats.total}
                prefix={<Badge color="blue" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="有效"
                value={stats.active}
                prefix={<Badge color="processing" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="已使用"
                value={stats.used}
                prefix={<Badge color="success" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="已过期"
                value={stats.expired}
                prefix={<Badge color="error" />}
              />
            </Card>
          </Col>
        </Row>

        {/* 搜索表单 */}
        <Card size="small" className="mb-4">
          <Form
            layout="inline"
            onFinish={handleSearch}
            initialValues={filters}
          >
            <Form.Item name="search">
              <Input
                placeholder="搜索收货码、服务器名称或用户名"
                prefix={<SearchOutlined />}
                style={{ width: 300 }}
              />
            </Form.Item>
            <Form.Item name="status">
              <Select placeholder="状态" style={{ width: 120 }} allowClear>
                <Option value="active">有效</Option>
                <Option value="used">已使用</Option>
                <Option value="expired">已过期</Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                  搜索
                </Button>
                <Button onClick={handleReset}>
                  重置
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => loadCodes(pagination.current, pagination.pageSize, filters)}>
                  刷新
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 操作按钮 */}
        <div className="mb-4">
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              创建收货码
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCode(null);
                form.resetFields();
                form.setFieldsValue({ count: 10 });
                setModalVisible(true);
              }}
            >
              批量创建
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => setExportModalVisible(true)}
            >
              批量导出
            </Button>
            {selectedRowKeys.length > 0 && (
              <>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setBatchModalVisible(true)}
                >
                  批量删除 ({selectedRowKeys.length})
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={codes}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          onChange={handleTableChange}
          rowSelection={rowSelection}
          scroll={{ x: 1500 }}
          size="small"
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingCode ? '编辑收货码' : '创建收货码'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="code"
            label="收货码"
            rules={[{ required: false, message: '请输入收货码' }]}
            tooltip="留空将自动生成收货码"
          >
            <Input placeholder="留空将自动生成收货码" />
          </Form.Item>

          <Form.Item
            name="count"
            label="批量创建数量"
            tooltip="仅在批量创建时使用，单个创建时忽略此字段"
          >
            <InputNumber 
              min={1} 
              max={100} 
              placeholder="批量创建数量" 
              style={{ width: '100%' }}
            />
          </Form.Item>


          <Form.Item
            name="status"
            label="状态"
            initialValue="active"
          >
            <Select>
              <Option value="active">有效</Option>
              <Option value="used">已使用</Option>
              <Option value="expired">已过期</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="expires_at"
            label="过期时间"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="选择过期时间（可选）"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea
              rows={3}
              placeholder="请输入备注信息（可选）"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCode ? '更新' : '创建'}
              </Button>
              <Button onClick={handleCloseModal}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量删除确认模态框 */}
      <Modal
        title="批量删除确认"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setBatchModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleBatchDelete}
          >
            确定删除
          </Button>
        ]}
      >
        <p>确定要删除选中的 {selectedRowKeys.length} 个收货码吗？</p>
        <p style={{ color: '#ff4d4f' }}>此操作不可撤销！</p>
      </Modal>

      {/* 批量导出模态框 */}
      <Modal
        title="导出收货码"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={exportForm}
          layout="vertical"
          onFinish={handleExport}
          initialValues={{
            status: '',
            prefix_text: '您的收货码：',
            suffix_text: '    感谢你支持悠悠鸟游戏，使用后即可开通使用服务器面板的功能。'
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态筛选"
              >
                <Select placeholder="选择状态" allowClear>
                  <Option value="">全部</Option>
                  <Option value="active">有效</Option>
                  <Option value="used">已使用</Option>
                  <Option value="expired">已过期</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="search"
                label="搜索关键词"
              >
                <Input placeholder="搜索收货码或备注" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="prefix_text"
            label="前缀文本"
          >
            <Input placeholder="收货码前面的文本" />
          </Form.Item>

          <Form.Item
            name="suffix_text"
            label="后缀文本"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="收货码后面的文本"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={exportLoading}>
                导出收货码
              </Button>
              <Button onClick={() => setExportModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TaobaoReceiptCodes;
